import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ChevronDown, ChevronUp, PlusCircle, Search, Ticket, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';
import { useUser } from '../../context/UserContext';
import { supportAPI } from '../../services/supportAPI';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const FAQS = [
  {
    question: "How do I book a retiring room?",
    answer: "You can book a retiring room by selecting a station, choosing your room category, and completing the payment online. Ensure you have a valid PNR number."
  },
  {
    question: "What is the cancellation and refund policy?",
    answer: "Cancellations made 48 hours before check-in are eligible for a full refund (minus gateway charges). Contact support for refund processing."
  },
  {
    question: "What ID proofs are accepted during check-in?",
    answer: "We accept Aadhaar Card, PAN Card, Voter ID, Passport, and Driving License. Please carry the original ID provided during booking."
  },
  {
    question: "Can I extend my stay?",
    answer: "Extensions are subject to availability. Please approach the station admin or raise a ticket before your checkout time."
  }
];

const STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-800 border border-blue-200',
  'Assigned': 'bg-indigo-100 text-indigo-800 border border-indigo-200',
  'In Progress': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
  'Waiting for Customer': 'bg-orange-100 text-orange-800 border border-orange-200',
  'Resolved': 'bg-green-100 text-green-800 border border-green-200',
  'Closed': 'bg-gray-100 text-gray-800 border border-gray-200',
  'Reopened': 'bg-purple-100 text-purple-800 border border-purple-200',
};

const HelpCenter = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const { isAuthenticated, loading: authLoading } = useUser();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      const fetchTickets = async () => {
        try {
          const res = await supportAPI.getMyTickets();
          setTickets(res.data.tickets);
        } catch (err) {
          console.error('Failed to fetch user tickets:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchTickets();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  return (
    <PublicLayout>
      <SEO title="Help Center | Retiring Room" description="Find answers to common questions and raise support tickets." />

      <div className="min-h-screen bg-midnight-50 py-12">
        <div className="max-w-6xl mx-auto px-4">

          <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-4">
            <div>
              <h1 className="text-4xl font-display font-bold text-midnight-900 mb-2">Help Center</h1>
              <p className="text-midnight-600 text-lg">Browse FAQs or manage your support tickets.</p>
            </div>
            <div className="flex gap-3">
              <Link to="/support/track" className="btn-secondary flex items-center gap-2">
                <Search className="w-4 h-4" /> Track Ticket
              </Link>
              <Link to="/support/new" className="btn-primary flex items-center gap-2">
                <PlusCircle className="w-4 h-4" /> Create Ticket
              </Link>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column: Tickets */}
            <div className="lg:col-span-1">
              <div className="card p-6 h-full flex flex-col bg-white">
                <h2 className="text-xl font-bold text-midnight-900 mb-4 flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-railway-500" />
                  My Tickets
                </h2>

                {authLoading || loading ? (
                  <div className="flex justify-center py-10"><LoadingSpinner /></div>
                ) : !isAuthenticated ? (
                  <div className="text-center py-10 bg-midnight-50 rounded-xl border border-midnight-100 flex-grow flex flex-col items-center justify-center">
                    <AlertCircle className="w-10 h-10 text-midnight-300 mb-3" />
                    <p className="text-midnight-600 mb-4">Log in to view your tickets</p>
                    <Link to="/user/login" className="btn-secondary py-2 text-sm">Login</Link>
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="text-center py-10 bg-midnight-50 rounded-xl border border-midnight-100 flex-grow flex flex-col items-center justify-center">
                    <Ticket className="w-10 h-10 text-midnight-300 mb-3" />
                    <p className="text-midnight-600 mb-4 text-sm">You haven't created any tickets yet.</p>
                    <Link to="/support/new" className="text-railway-600 font-semibold hover:underline text-sm">Create your first ticket</Link>
                  </div>
                ) : (
                  <div className="space-y-3 overflow-y-auto flex-grow pr-2" style={{ maxHeight: '600px' }}>
                    {tickets.map(ticket => (
                      <Link key={ticket._id} to={`/support/ticket/${ticket.ticketNumber}`} className="block bg-midnight-50 border border-midnight-100 p-4 rounded-xl hover:border-railway-300 transition-colors shadow-sm group">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-mono text-xs font-bold text-midnight-500 group-hover:text-railway-600 transition-colors">{ticket.ticketNumber}</span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_COLORS[ticket.status]}`}>
                            {ticket.status}
                          </span>
                        </div>
                        <h3 className="font-semibold text-midnight-900 text-sm mb-1 truncate">{ticket.subject}</h3>
                        <div className="flex items-center gap-1 text-xs text-midnight-400">
                          <Clock className="w-3 h-3" />
                          {new Date(ticket.createdAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: FAQ */}
            <div className="lg:col-span-2">
              <div className="card p-8 h-full bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <HelpCircle className="w-6 h-6 text-railway-500" />
                  <h2 className="text-2xl font-display font-bold text-midnight-900">Frequently Asked Questions</h2>
                </div>

                <div className="space-y-4">
                  {FAQS.map((faq, index) => (
                    <div key={index} className="border border-midnight-100 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setOpenIndex(openIndex === index ? null : index)}
                        className="w-full px-6 py-4 flex items-center justify-between bg-midnight-50 hover:bg-midnight-100 transition-colors text-left"
                      >
                        <span className="font-semibold text-midnight-900">{faq.question}</span>
                        {openIndex === index ? <ChevronUp className="w-5 h-5 text-midnight-500 shrink-0" /> : <ChevronDown className="w-5 h-5 text-midnight-500 shrink-0" />}
                      </button>
                      {openIndex === index && (
                        <div className="px-6 py-4 bg-white text-midnight-600 text-sm leading-relaxed">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-railway-50 rounded-xl border border-railway-100 text-center">
                  <h3 className="font-bold text-midnight-900 mb-2">Still need help?</h3>
                  <p className="text-midnight-600 text-sm mb-4">Our support team is always ready to help you with any issues.</p>
                  <p className="text-midnight-900 font-semibold">+91 89830 44786, +91 86004 47868</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </PublicLayout>
  );
};

export default HelpCenter;
